import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { openCalendly } from "@/integrations/calendly";

const components = [
  {
    title: "Capture",
    desc: "Every inquiry is captured the moment it arrives — portal lead, website form, call, or open-house sign-in.",
  },
  {
    title: "Respond",
    desc: "New buyer and seller leads get a fast, reliable first response — even while you're at a showing.",
  },
  {
    title: "Qualify",
    desc: "Timeline, financing readiness, and motivation are identified so you know who is ready to move.",
  },
  {
    title: "Follow Up",
    desc: "Prospects who aren't ready yet hear from you again, consistently, until they book or decline.",
  },
  {
    title: "Book",
    desc: "Qualified prospects can easily schedule a showing, consultation, or listing appointment.",
  },
  {
    title: "Track",
    desc: "You can see what happened to every lead — nothing disappears quietly.",
  },
];

export default function GrowthSystem() {
  return (
    <section id="growth-system" data-testid="growth-system-section" className="py-20 md:py-28 bg-[#0D1220]">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">
            The iVIBES <span className="text-gradient">Lead Conversion System™</span>
          </h2>
          <p className="mt-4 text-lg md:text-xl text-slate-200 leading-relaxed">
            When someone inquires about a listing or reaches out about selling, iVIBES helps make
            sure they don't get forgotten.
          </p>
          <p className="mt-3 text-base md:text-lg text-slate-300 leading-relaxed">
            The system can capture the inquiry, respond quickly, qualify the lead, follow up
            consistently, help the prospect book, and give you a clearer view of what happened to
            each opportunity.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {components.map((c, i) => (
            <motion.div
              key={c.title}
              data-testid={`growth-step-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-7 transition-[border-color] duration-300 hover:border-cyan-400/30"
            >
              <span className="h-9 w-9 rounded-full bg-cyan-500/15 text-cyan-300 font-heading font-bold text-sm flex items-center justify-center mb-5">
                {i + 1}
              </span>
              <h3 className="font-heading font-bold text-xl text-white mb-2.5">{c.title}</h3>
              <p className="text-slate-300 text-base leading-relaxed">{c.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.p
          data-testid="growth-system-positioning"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="mt-12 max-w-2xl mx-auto text-center text-lg text-slate-200 leading-relaxed"
        >
          The technology works behind the scenes. Your customer simply experiences a business that
          is responsive, organized, and easy to work with.
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-10 text-center"
        >
          <button
            type="button"
            onClick={() => openCalendly()}
            data-testid="growth-system-cta-button"
            data-track="cta_see_if_fits"
            className="group inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 bg-indigo-600 hover:bg-indigo-500 font-semibold text-white transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-[0_0_24px_rgba(99,102,241,0.45)]"
          >
            See If This Fits Your Business
            <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
